/**
 * Grade and progress helpers for course enrollments and problem XML.
 */

/**
 * Counts problem question tags in an OLX XML string.
 *
 * @param {string} xmlStr Problem XML.
 * @returns {number}
 */
export const getNumberOfQuestionsFromXMLString = (xmlStr) => {
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlStr, "text/xml");
  const questionList = ["optionresponse", "multiplechoiceresponse", "choiceresponse", "numericalresponse", "stringresponse"];
  const items = xml.querySelector("problem")?.children;

  if (!items || !items.length) return 0;

  let counter = 0;
  for (let i = 0; i < items.length; i++) {
    const tagName = items[i].tagName ? items[i].tagName.toLowerCase() : null;
    if (!tagName) continue;

    if (questionList.indexOf(tagName) >= 0) {
      counter++;
    }
  }

  return counter;
};

/**
 * Returns the question at `index` after sorting by `id`.
 *
 * @param {Array} questions Question map entries.
 * @param {number} index Zero-based index.
 * @returns {Object|null}
 */
export const getQuestionMapFromIndex = (questions, index) => {
  if (!questions) return null;
  questions = questions.sort((a, b) => (a.id > b.id ? 1 : -1));
  return questions[index];
};

/**
 * Builds a problem XBlock usage ID from a course ID and block ID.
 *
 * @param {string} courseId Course usage key.
 * @param {string} blockId Problem block identifier.
 * @returns {string}
 */
export const getModuleIdFromCourseId = (courseId, blockId) => {
  if (!courseId) return "";

  const idSplit = courseId.split(":");
  if (!idSplit || idSplit.length < 2) return "";

  const idBody = courseId.replace(idSplit[0], "block-v1");
  return `${idBody}+type@problem+block@${blockId}`;
};

/**
 * Computes weighted course progress from enrollment grades and graders.
 *
 * @param {Object} enroll Enrollment with `grades`, `graders`, and `moduleMapper`.
 * @returns {number} Progress percent.
 */
export const calcProgressValue = (enroll) => {
  if (!enroll || !enroll.grades) return 0;

  // Get earned / possible points per each module inside course: { module, earned, possible }
  let moduleGrades = [];
  for (let g of enroll.grades) {
    const idx = moduleGrades.findIndex((m) => m.module === g.section_title);

    if (idx < 0) {
      moduleGrades.push({
        module: g.section_title,
        earned: Number(g.state["raw_earned"]),
        possible: Number(g.state["raw_possible"]),
      });
    } else {
      moduleGrades[idx]["earned"] += Number(g.state["raw_earned"]);
      moduleGrades[idx]["possible"] += Number(g.state["raw_possible"]);
    }
  }

  // Calc progress value
  let progress = 0.0;
  const graders = enroll.graders || [];

  if (!graders) return 0.0;

  graders.forEach((g) => {
    const moduleTitles = enroll.moduleMapper.filter((m) => m["gradeFormat"] === g["type"]);

    let subProgress = 0.0;
    for (let moduleTitle of moduleTitles) {
      const modules = moduleGrades.filter((m) => m["module"] === moduleTitle["sectionTitle"]);
      let earned = 0,
        possible = 0;
      modules.forEach((m) => {
        earned += m["earned"];
        possible += m["possible"];
      });
      subProgress += possible ? Math.round((earned * 100.0) / parseFloat(possible)) : 0.0;
    }
    progress += (moduleTitles.length ? subProgress / moduleTitles.length : 0.0) * parseFloat(g["weight"]);
  });

  return progress;
};

/**
 * Maps enrollment grades onto module rows with earned/possible and pass flags.
 *
 * @param {Object} enroll Enrollment with `grades`, `graders`, and `moduleMapper`.
 * @returns {Array<Object>} Section grade details.
 */
export const mapSectionToGradeDetails = (enroll) => {
  if (!enroll || !enroll.grades) return [];

  const graders = enroll.graders || [];

  // Get earned / possible points per each module inside course: { module, earned, possible }
  // let moduleGrades = enroll.moduleMapper.map(a => {return {...a}});
  let moduleGrades = JSON.parse(JSON.stringify(enroll.moduleMapper)) || [];
  for (let g of enroll.grades) {
    const idx = moduleGrades.findIndex((m) => m.sectionTitle === g.section_title);

    if (idx < 0) {
      moduleGrades.push({
        sectionTitle: g.sectionTitle,
        raw_earned: Number(g.state["raw_earned"]),
        raw_possible: Number(g.state["raw_possible"]),
        details: [
          {
            earned: Number(g.state["raw_earned"]),
            possible: Number(g.state["raw_possible"]),
          },
        ],
      });
    } else {
      if (!moduleGrades[idx]["raw_earned"]) moduleGrades[idx]["raw_earned"] = 0;
      if (!moduleGrades[idx]["raw_possible"]) moduleGrades[idx]["raw_possible"] = 0;
      if (!moduleGrades[idx]["details"]) moduleGrades[idx]["details"] = [];
      moduleGrades[idx]["raw_earned"] += Number(g.state["raw_earned"]);
      moduleGrades[idx]["raw_possible"] += Number(g.state["raw_possible"]);
      moduleGrades[idx]["details"].push({
        earned: Number(g.state["raw_earned"]),
        possible: Number(g.state["raw_possible"]),
      });
    }
  }

  const pass = getGradeCutoff(enroll);

  for (let i = 0; i < moduleGrades.length; i++) {
    const graderItem = graders.find((g) => g["type"] === moduleGrades[i]["gradeFormat"]);
    const cutoff = graderItem ? parseFloat(graderItem["weight"]) : 0;
    const grade = moduleGrades[i]["raw_earned"] / parseFloat(moduleGrades[i]["raw_possible"]);

    let sameCount = enroll.moduleMapper ? enroll.moduleMapper.filter((m) => m.gradeFormat === moduleGrades[i]["gradeFormat"]).length : null;
    if (!sameCount) sameCount = 1;

    moduleGrades[i]["counter"] = i;
    moduleGrades[i]["grade"] = grade || 0;
    moduleGrades[i]["percent"] = (Math.round(grade * 100) * cutoff) / sameCount || 0;
    moduleGrades[i]["weight"] = (cutoff * 100) / sameCount;
    moduleGrades[i]["cutoff"] = cutoff;
    moduleGrades[i]["is_passed"] = grade > pass ? true : false;
    // moduleGrades[i]['is_passed'] = grade >= cutoff ? true : false;
  }

  return moduleGrades;
};

/**
 * Finds the grade record for a problem block in the current course.
 *
 * @param {Object} currCourse Course with a `grades` array.
 * @param {string} blockId Problem block identifier.
 * @returns {Object|null}
 */
export const getGradeForBlock = (currCourse, blockId) => {
  if (!currCourse) return null;
  if (!currCourse.grades) return null;
  return currCourse.grades.find((g) => g.module_id.includes(blockId));
};

/**
 * Returns the course passing cutoff, or 0 when the course is missing.
 *
 * @param {Object} course Course record.
 * @returns {number}
 */
export const getGradeCutoff = (course) => {
  if (!course) return 0.0;
  return course.cutoff;
};

/**
 * Counts fully correct problem blocks and whether every block has been graded.
 *
 * @param {Object} course Course with grades.
 * @param {Array<string>} xblockIds Problem block IDs.
 * @returns {{ counter: number, haveGraded: boolean }}
 */
export const getCorrectProblemBlockCount = (course, xblockIds) => {
  let counter = 0;
  // let haveGraded = false;
  let gradedCount = 0;
  for (let xId of xblockIds) {
    const grade = getGradeForBlock(course, xId);

    if (!grade || !grade.state || !grade.state.question_map) continue;

    if (grade.state.question_map.filter((m) => m.correctness === "correct").length === Number(grade.state.raw_possible) && grade.state.question_map.filter((m) => m.correctness === "correct").length !== 0) {
      counter++;
    }
    gradedCount++;
  }

  const haveGraded = gradedCount === xblockIds.length;

  return { counter, haveGraded };
};
